import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { parseISO, format } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EnhancedProject, getProjectById } from "../api/projectManagementApi";
import { Button } from "../components/ui/button";
import { TabNavigation } from "../components/ui/tab-navigation";
import { ProjectHeader } from "../components/ui/page-header";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import paths from "../routes";
import {
  Calendar,
  Clock,
  CheckCircle,
  Download,
  Upload,
  BookOpen,
  AlertCircle,
  Users,
  Target,
  FileText,
  Database,
  Play,
  Terminal,
  RefreshCw,
  CheckSquare,
  ExternalLink,
  Power,
  Settings,
  X,
  Plus,
  Trash2,
  Send,
  ArrowLeft,
  Clipboard,
  Check,
  User,
  Flag,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Eye,
  Rocket,
  Award,
  RotateCcw,
  Newspaper,
  Info,
  Cpu,
  HardDrive,
  Package,
  Server,
  UserCircle,
  Key,
  EyeOff,
  CloudLightning,
  Moon,
  Laptop,
  Settings2,
} from "lucide-react";
import ProjectMessagesChat from "../components/ProjectMessagesChat";
// import useAuth from "../components/Context/AuthContext";
// import useNavigation from "../components/Context/NavigationContext";
import { toast } from "sonner";
import { useAuth, useNavigation } from "../App";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { cn } from "../lib/utils";
import { LocalRunButton } from "../components/LocalRunButton";
import { AgentStatusBadge } from "../components/AgentStatusBadge";
import { SandboxPanel } from "../components/sandbox/SandboxPanel";
import {
  useCreateWorkspaceMutation,
  useStopJupyterServerMutation,
  useStopSandboxMutation,
  useCancelProvisioningMutation,
  useLocalRunCommandQuery,
  createWorkspace,
  workspaceApi,
  getActiveWorkspaceSession,
  getWorkspaceStatus,
  listSessions,
} from "../api/jupyterManagementApi";
import { useSandboxLifecycle } from "../hooks/useSandboxLifecycle";
import type { SandboxStatus } from "../hooks/useSandboxLifecycle";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SandboxTermsModal from "../components/SandboxTerms";
import axios from "axios";
import { useCheckpointAction } from "../api/createProjectApi";

interface Project {
  id: string;
  title: string;
  description: string;
  subject: string;
  instructor: string;
  dueDate: string;
  createdAt: string;
  status: "not_started" | "in_progress" | "submitted" | "graded";
  progress: number;
  grade?: number;
  totalPoints: number;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
  objectives: string[];
  requirements: string[];
  due_date?: string;
  estimated_hours?: number;
  project_overview?: string;
  learning_objectives?: string;
  prerequisites?: string;
  type?: string;
  students?: any[];
  teachingAssistants?: any[];
  instructors?: any[];
  sandbox_time_duration?: string;
  closed_project?: boolean;
  datasets: Dataset[];
  submissions: Submission[];
  rubrics: RubricItem[];
  estimatedHours: number;
  tags: string[];
  checkpoints: Checkpoint[];
  total_points: number;
  late_submission_allowed?: boolean;
  late_penalty_percent?: string;
  max_attempts?: number;
  difficulty_level?: string;
  instructions?: string;
}

interface Checkpoint {
  id: string;
  title: string;
  description: string;
  status: "not_started" | "in_progress" | "submitted" | "graded" | "overdue";
  due_date: string;
  submittedDate?: string;
  grade?: string;
  instructorFeedback?: string;
  rubrics?: Array<{
    id: string;
    title: string;
    description: string;
    criteria: Array<{
      name: string;
      description: string;
      points: number;
    }>;
  }>;
}

interface Dataset {
  id: string;
  name: string;
  description: string;
  format: string;
  size: string;
  downloadUrl: string;
}

interface Submission {
  id: string;
  submittedAt: string;
  files: string[];
  grade?: number;
  feedback?: string;
  status: "submitted" | "graded" | "revision_needed";
}

interface RubricItem {
  id: string;
  title: string;
  criteria: any[];
  description: string;
  total_points: number;
  earnedPoints?: number;
}


interface SubmissionFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
}

interface TeamMember {
  name: string;
  email: string;
  avatar: string;
}

interface instructor {
  name: string;
  role: "TA" | "Instructor";
  email: string;
}

function formatProjectForWorkspace(project: any) {
  return {
    id: project?.id,
    title: project?.title,
    courseName: project?.course?.name,
    createdAt: project?.createdAt ?? null,
    attempts: project?.max_attempts ?? 0,
    creatorName: project?.creator?.name ?? null,
    checkpoints: Array.isArray(project?.checkpoints)
      ? project?.checkpoints
          .filter((cp: any) => cp?.id && cp?.title)
          .map((cp: any) => ({ id: cp.id, title: cp.title }))
      : [],
    isGroupProject: project?.is_group_project ?? false,
    students: project?.students ?? [],
  };
}

function parseRemainingSandboxTimeToSeconds(value?: string | null): number | null {
  if (!value || typeof value !== "string") return null;
  const match = value.match(/^(\d+):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return Math.max(0, hours * 3600 + minutes * 60);
}

function formatSecondsToHHMM(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  return `${hours.toString().padStart(3, "0")}:${minutes.toString().padStart(2, "0")}`;
}


function SubmissionDialog({
  project,
  open,
  onOpenChange,
  onSubmit,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (files: SubmissionFile[], comments: string) => void;
}) {
  const [files, setFiles] = useState<SubmissionFile[]>([]);
  const [comments, setComments] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList: FileList) => {
    const newFiles: SubmissionFile[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file: any = fileList[i];
      const submissionFile: SubmissionFile = {
        id: `file_${Date.now()}_${i}`,
        name: file.name,
        size: file.size,
        type: file.type || "application/octet-stream",
        file: file,
      };
      newFiles.push(submissionFile);
    }

    setFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSubmit = () => {
    if (files.length === 0) {
      toast.error("Please upload at least one file");
      return;
    }

    onSubmit(files, comments);
    // Reset form
    setFiles([]);
    setComments("");
    onOpenChange(false);
  };

  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const maxSize = 100 * 1024 * 1024;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white border">
        <DialogHeader>
          <DialogTitle>Submit Work - {project?.title}</DialogTitle>
          <DialogDescription>
            Upload your project files and add submission comments
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Project Info */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-900">Due Date:</span>
                <span className="ml-2 text-blue-800">
                  {project?.due_date
                    ? new Date(project.due_date).toLocaleDateString()
                    : "N/A"}{" "}
                  at{" "}
                  {project?.due_date
                    ? new Date(project.due_date).toLocaleTimeString()
                    : "N/A"}
                </span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Total Points:</span>
                <span className="ml-2 text-blue-800">
                  {project?.total_points} points
                </span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Subject:</span>
                <span className="ml-2 text-blue-800">{project?.subject}</span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Instructor:</span>
                <span className="ml-2 text-blue-800">
                  {project?.instructor}
                </span>
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Upload Files</Label>
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? "border-bits-blue bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900">
                  Drop files here or click to browse
                </p>
                <p className="text-sm text-gray-500">
                  Supported formats: .ipynb, .py, .pdf, .csv, .png, .jpg, .txt,
                  .md
                </p>
                <p className="text-xs text-gray-400">
                  Maximum total size: 100MB
                </p>
              </div>
              <input
                type="file"
                multiple
                onChange={handleChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                accept=".ipynb,.py,.pdf,.csv,.png,.jpg,.jpeg,.txt,.md,.docx,.xlsx,.zip"
              />
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">
                  Uploaded Files ({files.length})
                </Label>
                <div className="text-sm text-gray-500">
                  Total: {formatFileSize(totalSize)} / {formatFileSize(maxSize)}
                </div>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {files?.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {file.type}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(file.id)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {totalSize > maxSize && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">
                    Total file size exceeds the 100MB limit. Please remove some
                    files.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Comments */}
          <div className="space-y-2">
            <Label htmlFor="comments" className="text-base font-medium">
              Submission Comments (Optional)
            </Label>
            <Textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add any comments about your submission, challenges faced, or additional notes for the instructor..."
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Submission Guidelines */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">
              Submission Guidelines
            </h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>
                • Ensure all required files are included and properly named
              </li>
              <li>
                • Include a main notebook (.ipynb) file with your complete
                analysis
              </li>
              <li>• Add comments and documentation to your code</li>
              <li>
                • Test your code before submission to ensure it runs without
                errors
              </li>
              <li>• Include any additional resources or references used</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={files.length === 0 || totalSize > maxSize}
              className="bg-bits-blue hover:bg-bits-blue/90"
            >
              <Send className="h-4 w-4 mr-2" />
              Submit Work
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ProjectDetailsProps {
  project: Project;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ project }) => {
  const dueDate = project?.due_date ? new Date(project.due_date) : new Date();
  const now = new Date();
  const daysLeft = Math.ceil(
    (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
  const dueInfo = {
    urgent: daysLeft <= 3,
    text:
      daysLeft > 0
        ? `${daysLeft} days left`
        : daysLeft === 0
          ? "Due today"
          : "Overdue",
  };
  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Description */}
          <Card className="gap-4">
            <CardHeader>
              <div className="flex">
                <div className="h-6 w-8">
                  <FileText className="h-6 w-6 text-bits-blue" />
                </div>
                <CardTitle className="text-xl text-bits-blue font-semibold mt-0">
                  Project Description
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="ml-1">
              <div
                className="text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                dangerouslySetInnerHTML={{
                  __html: project?.description || "-",
                }}
              />
            </CardContent>
          </Card>

          {/* Project Overview */}
          <Card className="gap-4">
            <CardHeader>
              <div className="flex">
                <div className="h-6 w-8 ">
                  <Clipboard className=" h-6 w-6 text-bits-blue" />
                </div>
                <CardTitle className="text-xl text-bits-blue font-semibold mt-0">
                  Project Overview
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="ml-1">
              <div
                className="text-bits-grey-600 mb-6
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                dangerouslySetInnerHTML={{
                  __html: project?.project_overview || "-",
                }}
              />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {project?.estimated_hours
                      ? `${project.estimated_hours}h`
                      : "N/A"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Estimated Time
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {project?.total_points}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Total Points
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold capitalize">
                    {project?.difficulty_level}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Difficulty
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {project?.max_attempts || "N/A"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Max Attempts
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Learning Objectives */}
          <Card className="gap-4">
            <CardHeader>
              <CardTitle className="flex text-xl text-bits-blue items-center font-semibold">
                <Target className="h-6 w-6 mr-2  text-bits-green" />
                Learning Objectives
              </CardTitle>
            </CardHeader>
            <CardContent className="ml-1">
              <div
                className="text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                dangerouslySetInnerHTML={{
                  __html: project?.learning_objectives || "-",
                }}
              />
            </CardContent>
          </Card>

          {/* Requirements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex text-xl text-bits-blue items-center font-semibold">
                <BookOpen className="h-6 w-6 mr-3 text-blue-500" />
                Prerequisites & Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="ml-1">
              <div
                className="text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                dangerouslySetInnerHTML={{
                  __html: project?.prerequisites || "-",
                }}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex text-xl text-bits-blue items-center font-semibold">
                <Newspaper className="h-6 w-6 mr-3 text-blue-500" />
                Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="ml-1">
              <div
                className="text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                dangerouslySetInnerHTML={{
                  __html: project?.instructions || "-",
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Due Date */}
          <Card>
            <CardHeader>
              <CardTitle className="flex text-xl text-bits-blue items-center font-semibold">
                <Calendar className="h-6 w-6 mr-2 text-bits-red" />
                Upcoming Due Date
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-2xl font-bold mb-3">
                  {project?.due_date
                    ? (() => {
                        const date = parseISO(project?.due_date);
                        return isNaN(date.getTime())
                          ? "Invalid Date"
                          : format(date, "dd/MM/yyyy");
                      })()
                    : "N/A"}
                </div>
                <Badge
                  variant={dueInfo.urgent ? "destructive" : "secondary"}
                  className={
                    dueInfo.urgent ? "!bg-red-600 text-white" : undefined
                  }
                >
                  {dueInfo.text}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Team Members */}
          {/* <Card className="gap-3">
            <CardHeader>
              <CardTitle className="flex text-xl items-center font-semibold">
                <User className="h-7 w-7 mr-2 text-bits-warning" />
                Team Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              {teamMembers.map((member: TeamMember, index: number) => (
                <div
                  key={index}
                  className="flex items-center space-x-4 space-y-4"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={member.avatar}
                      alt={`@${member.name}`}
                      className="rounded-full"
                    />
                  </Avatar>
                  <div>
                    <p className="font-medium">{member.name}</p>
                    <p className="text-muted-foreground">{member.email}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card> */}
          {/* Group Configuration Section - Added */}
          <div className="mt-8">
            {project?.type === "group" &&
              (project?.students?.length ?? 0) > 0 &&
              project?.students?.some((student) => student.groupId) && (
                <Card className="gap-3">
                  <CardHeader>
                    <CardTitle className="flex text-xl items-center font-semibold">
                      <Users className="h-6 w-6 mr-3 text-blue-500" />
                      Team Members
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {project.students
                      .filter((student) => student.groupId)
                      .map((group) => (
                        <div
                          key={group?.groupId}
                          className="space-y-3 pb-4 border-b last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-lg">
                                {group?.groupName}
                              </p>
                              <p className="text-sm text-muted-foreground flex items-center space-x-1">
                                <span>{group?.groupCode}</span>
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                <span>{group?.groupCategoryName}</span>
                              </p>
                            </div>
                            <div className="ml-2 text-sm text-muted-foreground">
                              {group?.currentSize}/{group?.maxSize} members
                            </div>
                          </div>

                          {group?.members?.length > 0 && (
                            <div className="space-y-2 ml-4">
                              {group.members.map((member) => (
                                <div key={member?.id} className="flex flex-col">
                                  <div className="flex items-center space-x-2">
                                    <p className="font-medium">
                                      {member?.name}
                                    </p>
                                    {member?.role === "leader" && (
                                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                        Leader
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-muted-foreground text-sm flex items-center space-x-1">
                                    <span className="capitalize">
                                      {member?.role}
                                    </span>
                                    <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                    <span
                                      className="overflow-hidden text-ellipsis text-wrap"
                                      title={member?.email}
                                    >
                                      {member?.email}
                                    </span>
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                  </CardContent>
                </Card>
              )}
          </div>

          {/* TA & Instructors */}
          {((project?.teachingAssistants?.length ?? 0) > 0 ||
            (project?.instructors?.length ?? 0) > 0) && (
            <Card className="gap-3">
              <CardHeader>
                <CardTitle className="flex text-xl items-center font-semibold">
                  <Users className="h-6 w-6 mr-3 text-blue-500" />
                  TA & Instructors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 overflow-hidden text-ellipsis whitespace-nowrap">
                {project.teachingAssistants?.map((teacher, index) => (
                  <div key={index} className="flex flex-col">
                    <p className="font-medium">{teacher.name}</p>
                    <p className="text-muted-foreground flex items-center space-x-1">
                      <span>TA</span>
                      <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                      <span>{teacher.email}</span>
                    </p>
                  </div>
                ))}
                {project?.instructors?.map((teacher, index) => (
                  <div key={index} className="flex flex-col">
                    <p className="font-medium">{teacher?.name}</p>
                    <p className="text-muted-foreground flex items-center space-x-1">
                      <span>Instructor</span>
                      <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                      <span
                        className="overflow-hidden text-ellipsis whitespace-nowrap"
                        title={teacher?.email}
                      >
                        {teacher?.email}
                      </span>
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Project Information */}
          <Card className="gap-3">
            <CardHeader>
              <CardTitle className="flex text-xl items-center font-semibold">
                <Info className="h-6 w-6 mr-3 text-blue-500" />
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Max Attempts:</span>
                <span className="text-sm font-semibold text-blue-600">
                  {project?.max_attempts || "N/A"}
                </span>
              </div> */}

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Sandbox Hours:
                </span>
                <span className="text-sm font-semibold text-blue-600">
                  {project?.sandbox_time_duration
                    ? `${project.sandbox_time_duration}h`
                    : "N/A"}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Project Type:
                </span>
                <span className="text-sm font-semibold capitalize text-blue-600">
                  {project?.type || "N/A"}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Late Submission:
                </span>
                <span
                  className={`text-sm font-semibold ${
                    project?.late_submission_allowed
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {project?.late_submission_allowed ? "Allowed" : "Not Allowed"}
                </span>
              </div>

              {/* {project?.type && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    Type:
                  </span>
                  <span className="text-sm font-semibold text-blue-600 capitalize">
                    {project?.type}
                  </span>
                </div>
              )} */}

              {project?.tags && project.tags.length > 0 && (
                <div className="pt-2 border-t">
                  <span className="text-sm font-medium text-gray-700 block mb-2">
                    Tags:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag: string, index: number) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="bg-blue-100 text-blue-800 border-blue-300"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};


interface CheckpointsAndSubmissionsProps {
  project: EnhancedProject;
}

const CheckpointsAndSubmissions: React.FC<CheckpointsAndSubmissionsProps> = ({
  project,
}) => {
  const { user } = useAuth();
  const { navigateTo } = useNavigation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspace, updateWorkspace, resetWorkspace, isConnectionVerified } = useJupyterWorkspace();
  const queryClient = useQueryClient();

  // React Query mutations
  const createWorkspaceMutation = useCreateWorkspaceMutation();
  const stopJupyterServerMutation = useStopJupyterServerMutation();
  const checkpointActionMutation = useCheckpointAction();

  // Checkpoint-scoped sandbox state (course-project specific)
  const [activeSandboxCheckpoint, setActiveSandboxCheckpoint] = useState<string | null>(null);
  const [agreed, setAgreed] = useState<boolean>(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [provisioningStartedAt, setProvisioningStartedAt] = useState<number | null>(null);
  const [sandboxTimeExhausted, setSandboxTimeExhausted] = useState(false);
  const launchDisabledReason = sandboxTimeExhausted
    ? "Sandbox time exhausted for this project. Contact your instructor for an extension."
    : undefined;
  const remainingSandboxTimeLabel =
    typeof (project as any)?.remaining_sandbox_time === "string" &&
    (project as any)?.remaining_sandbox_time
      ? (project as any).remaining_sandbox_time
      : typeof workspace.remainingTime === "number"
        ? formatSecondsToHHMM(workspace.remainingTime)
        : "N/A";

  // ── Sandbox lifecycle (unified hook) ──────────────────────────────────────
  const sandboxLifecycle = useSandboxLifecycle({
    sandboxId: project?.id || projectId || "",
    launchFn: () => createWorkspaceMutation.mutateAsync({ projectId: project?.id || projectId || "" }),
    onActive: async (data) => {
      let polledKernelId = data.kernel?.id || "";
      let polledNotebookPath = data.notebookPath;
      if (!polledKernelId) {
        try {
          const sessions = await listSessions();
          const match = sessions.find((s: any) => s.path?.includes(project?.id));
          if (match?.kernel?.id) {
            polledKernelId = match.kernel.id;
            if (!polledNotebookPath && match.path) polledNotebookPath = `/${match.path}`;
          }
        } catch (_) { /* ignore */ }
      }
      updateWorkspace({
        project: formatProjectForWorkspace(project),
        kernelId: polledKernelId,
        sessionId: data.sessionId || null,
        workspaceUrl: data.server?.url || "",
        status: "active",
        instanceId: data.server?.state?.object_name || "",
        createdAt: data.server?.started || new Date().toISOString(),
        lastActivity: data.kernel?.last_activity || new Date().toISOString(),
        folderPath: data.folderPath,
        notebookPath: polledNotebookPath,
        serverReady: data.server?.ready || false,
        kernelExecutionState: data.kernel?.execution_state || "unknown",
        remainingTime: data?.remainingTime ?? null,
        totalAllowedTime: data?.totalAllowedTime ?? null,
        timeUsed: data?.timeUsed ?? null,
      });
    },
    onIdle: () => resetWorkspace(),
    onProvisioning: () => {
      if (workspace.status !== "provisioning") {
        updateWorkspace({ 
          status: "provisioning", 
          project: formatProjectForWorkspace(project) 
        });
      }
    },
    sessionId: workspace.sessionId,
    userIdentifier: `user_${user?.id}`,
    launchDisabledReason,
  });

  const {
    sandboxStatus,
    setSandboxStatus,
    handleLaunch: handleLaunchFromHook,
    handleCancel,
    handleStop,
    isSandboxTimeExhausted,
    setIsSandboxTimeExhausted,
  } = sandboxLifecycle;

  // Sync sandboxTimeExhausted from hook back to local state
  useEffect(() => {
    setSandboxTimeExhausted(isSandboxTimeExhausted);
  }, [isSandboxTimeExhausted]);

  // ── Sync workspace context → sandboxStatus on mount/context change ──────────
  useEffect(() => {
    const isActiveOrStopping = sandboxStatus.status === "provisioning" || sandboxStatus.status === "stopping";
    if (isActiveOrStopping) return;
    if (workspace.project?.id === project?.id && workspace.status !== "none") {
      setSandboxStatus({ status: workspace.status as any });
      setActiveSandboxCheckpoint(null);
    } else if (workspace.project?.id === project?.id && workspace.status === "none") {
      setSandboxStatus({ status: "none" });
      setActiveSandboxCheckpoint(null);
    } else if (workspace.project?.id !== project?.id) {
      setSandboxStatus({ status: "none" });
      setActiveSandboxCheckpoint(null);
    }
  }, [workspace.project?.id, workspace.status, workspace.instanceId, project?.id]);

  // ── Sandbox time exhaustion guard ──────────────────────────────────────
  useEffect(() => {
    const remainingFromProject = parseRemainingSandboxTimeToSeconds(
      (project as any)?.remaining_sandbox_time,
    );
    if (remainingFromProject !== null) {
      const exhausted = remainingFromProject <= 0;
      setSandboxTimeExhausted(exhausted);
      setIsSandboxTimeExhausted(exhausted);
      return;
    }
    if (typeof workspace.remainingTime === "number") {
      const exhausted = workspace.remainingTime <= 0;
      setSandboxTimeExhausted(exhausted);
      setIsSandboxTimeExhausted(exhausted);
    }
  }, [project, workspace.remainingTime]);

  // ── On mount: probe for existing active workspace ──────────────────────────

  // On mount: reconcile Redis lease + orchestrator status with React context.
  // Heartbeats do not run on this route, so after idle timeout the context can
  // still say "active" while `active-ws:*` is already cleared — clear stale UI here.
  useEffect(() => {
    if (!project?.id || !isConnectionVerified) return;
    let cancelled = false;

    getActiveWorkspaceSession(project.id).then(async (active) => {
      if (cancelled) return;
      try {
        const probe = await getWorkspaceStatus(project.id);
        if (cancelled) return;

        const forThisProject = workspace.project?.id === project.id;
        const uiStaleSession =
          forThisProject &&
          (workspace.status === "active" || workspace.status === "error");
        // Hook-local panel can still be "active" after idle stop (no heartbeats on this route;
        // workspace may already be "none" while sandboxStatus stayed out of sync).
        const panelThinksLive =
          sandboxStatus.status === "active" || sandboxStatus.status === "error";

        if (!active) {
          // Align TanStack cache with "no lease" so useSandboxLifecycle cannot re-promote
          // from stale { status: "active" } after we clear the UI.
          const qcPayload =
            probe.status === "provisioning"
              ? probe
              : probe.status === "error"
                ? probe
                : { status: "idle" as const, progress: probe?.progress ?? 0, message: probe?.message };
          queryClient.setQueryData(["jupyter", "workspace", "status", project.id], qcPayload);

          // No `active-ws` lease — platform has no live session slot. Do not trust a lone
          // `bits:workspace` payload (it can outlive timeout/stop); never resurrect "active" UI.
          if (uiStaleSession || panelThinksLive) {
            resetWorkspace();
            setSandboxStatus({ status: "none" });
          } else if (probe.status === "provisioning") {
            setSandboxStatus({
              status: "provisioning",
              provisioningProgress: probe.progress || 10,
            });
            setProvisioningStartedAt(Date.now());
            updateWorkspace({
              status: "provisioning",
              project: formatProjectForWorkspace(project),
            });
          }
          return;
        }

        if (active && (probe.status === "idle" || probe.status === "error")) {
          resetWorkspace();
          setSandboxStatus({ status: "none" });
          return;
        }

        if (probe.status === "provisioning") {
          setSandboxStatus({
            status: "provisioning",
            provisioningProgress: probe.progress || 10,
          });
          setProvisioningStartedAt(Date.now());
          updateWorkspace({
            status: "provisioning",
            project: formatProjectForWorkspace(project),
          });
          return;
        }

        if (probe.status !== "active" || !probe.data) {
          return;
        }

        const wType = active?.workspaceType ?? "cloud";
        const effectiveType = workspace.globalSandboxMode === "cloud" ? "cloud" : wType;
        updateWorkspace({ workspaceType: effectiveType });

        const d = probe.data;
        // If status is active but backend has no kernel info (bits:workspace not yet written
        // for cloud sandboxes), recover the real kernel ID directly from the Jupyter server.
        let recoveredKernelId = d.kernel?.id || "";
        let recoveredNotebookPath = d.notebookPath;
        if (!recoveredKernelId) {
          try {
            const sessions = await listSessions();
            const match = sessions.find((s: any) =>
              s.path?.includes(project.id)
            );
            if (match?.kernel?.id) {
              recoveredKernelId = match.kernel.id;
              if (!recoveredNotebookPath && match.path) {
                recoveredNotebookPath = `/${match.path}`;
              }
            }
          } catch (_) { /* ignore — execute will show a clear error if still empty */ }
        }
        updateWorkspace({
          project: formatProjectForWorkspace(project),
          kernelId: recoveredKernelId,
          sessionId: d.sessionId || null,
          workspaceUrl: d.server?.url || "",
          status: "active",
          folderPath: d.folderPath,
          notebookPath: recoveredNotebookPath,
          serverReady: d.server?.ready || false,
          kernelExecutionState: d.kernel?.execution_state || "idle",
          remainingTime: d?.remainingTime ?? null,
          totalAllowedTime: d?.totalAllowedTime ?? null,
          timeUsed: d?.timeUsed ?? null,
        });
        setSandboxStatus({
          status: "active",
          provisioningProgress: 100,
          createdAt: new Date().toISOString(),
          lastActivity: new Date().toISOString(),
          autoSaveEnabled: true,
        });
      } catch (_) {
        // probe failed — leave UI as-is
      }
    }).catch(() => { /* silently ignore */ });

    return () => {
      cancelled = true;
    };
  }, [
    project?.id,
    isConnectionVerified,
    location.pathname,
    workspace.project?.id,
    workspace.status,
    sandboxStatus.status,
    queryClient,
    resetWorkspace,
    setSandboxStatus,
    updateWorkspace,
  ]);

  useEffect(() => {
    // Check if the workspace in the context belongs to the current project.
    // Don't override provisioning state that was started by handleLaunchSandbox or the mount-time auto-resume.
    const isRecentlyStarted = provisioningStartedAt && (Date.now() - provisioningStartedAt < 60000);
    if (sandboxStatus.status === "provisioning" || sandboxStatus.status === "stopping" || isRecentlyStarted) return;

    if (workspace.project?.id === project?.id && workspace.status !== "none") {
      setSandboxStatus({
        status: workspace.status,
      });
      setActiveSandboxCheckpoint(null);
    } else {
      setSandboxStatus({ status: "none" });
      setActiveSandboxCheckpoint(null);
    }
  }, [
    sandboxStatus.status,
    workspace.project?.id,
    workspace.status,
    workspace.instanceId,
    workspace.createdAt,
    workspace.lastActivity,
    workspace.errorMessage,
    project?.id,
    provisioningStartedAt,
  ]);


  type CheckpointStatus =
    | "not_started"
    | "in_progress"
    | "submitted"
    | "graded"
    | "overdue";

  const statusStyles: Record<CheckpointStatus, { text: string; bg: string }> = {
    graded: {
      text: "text-bits-blue",
      bg: "bg-indigo-100",
    },
    submitted: {
      text: "text-bits-light-green",
      bg: "bg-green-100",
    },
    overdue: {
      text: "text-bits-red",
      bg: "bg-red-100",
    },
    in_progress: {
      text: "text-bits-orange",
      bg: "bg-orange-100",
    },
    not_started: {
      text: "text-bits-orange",
      bg: "bg-orange-100",
    },
  };

  // API call function when a checkpoint is clicked
  const handleCheckpointClick = (checkpointId: string) => {
    if (!user?.id || !projectId) {
      // toast.error("Missing required information. Please refresh and try again.");
      return;
    }

    checkpointActionMutation.mutate(
      {
        userId: user.id,
        projectId: projectId,
        checkpointId: checkpointId,
      },
      {
        onSuccess: (data) => {
          // Store the checkpoint submission data
          if (data?.isSuccess && data?.data?.submission) {
            setCheckpointSubmissions((prev) => ({
              ...prev,
              [checkpointId]: data.data.submission,
            }));
          }
        },
      },
    );
  };

  // ── Thin wrappers using hook ─────────────────────────────────────────────
  const handleLaunchSandbox = async (userCheckpointId?: string) => {
    if (project?.closed_project) {
      toast.error("This project is closed. You cannot launch a sandbox.");
      return;
    }
    setActiveSandboxCheckpoint(userCheckpointId ?? null);
    await handleLaunchFromHook();
  };

  const handleOpenSandbox = () => {
    if (!user?.name) {
      toast.error("Cannot open sandbox. User not found.");
      return;
    }
    if (sandboxStatus.status !== "active") {
      toast.error("Sandbox is not active. Please launch it first.");
      return;
    }
    setSandboxStatus((prev) => ({ ...prev, lastActivity: new Date().toISOString() }));
    navigate(`${paths.notebook.replace(":projectId", project?.id || "")}`, {
      state: { 
        project: formatProjectForWorkspace(project),
        fromDetails: true
      },
    });
  };

  const handleStopSandbox = async () => {
    await handleStop();
  };

  const handleCancelLaunch = async () => {
    await handleCancel();
  };



  const handleDownloadResources = async () => {
    try {
      toast.info("Preparing download...");

      // Build dataset list from old-style direct S3 URLs and/or linked Dataset repository files
      let datasets: { presignedUrl: string; fileName: string }[] = [];

      if (Array.isArray(project?.dataset_s3_url) && project.dataset_s3_url.length > 0) {
        datasets = project.dataset_s3_url;
      }

      // Fallback: also include linked datasets whose s3_keys have a download_url
      if (datasets.length === 0 && Array.isArray((project as any)?.datasets)) {
        for (const ds of (project as any).datasets) {
          for (const fileInfo of ds.s3_keys || ds.s3Keys || []) {
            if (fileInfo?.download_url || fileInfo?.s3_url) {
              datasets.push({
                presignedUrl: fileInfo.download_url || fileInfo.s3_url,
                fileName: fileInfo.originalName || fileInfo.key?.split("/").pop() || "file",
              });
            }
          }
        }
      }

      if (datasets.length === 0) {
        toast.error("No datasets available for download.");
        return;
      }

      // If only one file, download directly
      if (datasets.length === 1) {
        const dataset = datasets[0];
        try {
          const response = await fetch(dataset.presignedUrl);
          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);

          const blob = await response.blob();
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = dataset.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);

          toast.success(`Downloaded: ${dataset.fileName}`);
        } catch (error) {
          toast.error(`Failed to download ${dataset.fileName}`);
        }
        return;
      }

      // For multiple files, create a zip
      toast.info("Creating zip file with all datasets...");

      // Import JSZip dynamically (you'll need to install it: npm install jszip)
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();

      // Download all files and add to zip
      const downloadPromises = datasets.map(async (dataset) => {
        try {
          const response = await fetch(dataset.presignedUrl);
          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);

          const blob = await response.blob();
          zip.file(dataset.fileName, blob);
          return { success: true, fileName: dataset.fileName };
        } catch (error) {
          return { success: false, fileName: dataset.fileName, error };
        }
      });

      const results = await Promise.all(downloadPromises);
      const successful = results.filter((r) => r.success);
      const failed = results.filter((r) => !r.success);

      if (failed.length > 0) {
        toast.error(`${failed.length} file(s) failed to download`);
      }

      if (successful.length === 0) {
        toast.error("Failed to download any files.");
        return;
      }

      // Add project information file
      const projectInfo = `Project: ${project?.title || "Untitled Project"}
          Description: ${project?.description || "No description available"}
          Course: ${project?.course?.name} (${project?.course?.code})
          Difficulty: ${project?.difficulty_level}
          Estimated Hours: ${project?.estimated_hours}
          Due Date: ${
            project?.due_date
              ? new Date(project.due_date).toLocaleDateString()
              : "Not specified"
          }

          Datasets included:
          ${successful.map((s) => `- ${s.fileName}`).join("\n")}

          ${
            failed.length > 0
              ? `\nFailed to include:\n${failed
                  .map((f) => `- ${f.fileName}`)
                  .join("\n")}`
              : ""
          }

          Generated on: ${new Date().toLocaleString()}
          `;

      zip.file("PROJECT_INFO.txt", projectInfo);

      // Generate and download zip
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${project?.title?.replace(
        /[^a-zA-Z0-9]/g,
        "_",
      )}_resources.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast.success(`Downloaded ${successful.length} dataset(s) in zip file`);
    } catch (error) {
      toast.error("Failed to download resources. Please try again.");
    }
  };
  const handleDownloadDataset = async (dataset: Dataset) => {
    try {
      toast.info(`Downloading ${dataset.name}...`);

      // Simulate individual file download
      setTimeout(() => {
        toast.success(`Downloaded: ${dataset.name}`);

        // Create a mock download
        const link = document.createElement("a");
        link.href =
          "data:text/plain;charset=utf-8," +
          encodeURIComponent(
            `Mock dataset: ${dataset.name}\nDescription: ${dataset.description}\nFormat: ${dataset.format}\nSize: ${dataset.size}`,
          );
        link.download = dataset.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 1000);
    } catch (error) {
      toast.error("Failed to download dataset. Please try again.");
    }
  };

  const [collapsedCheckpoints, setCollapsedCheckpoints] = useState<string[]>(
    [],
  );

  // State to store checkpoint submission data
  const [checkpointSubmissions, setCheckpointSubmissions] = useState<{
    [key: string]: any;
  }>({});

  // Initialize collapsed checkpoints when project data loads
  useEffect(() => {
    if (project?.checkpoints) {
      setCollapsedCheckpoints(project.checkpoints.map((cp: any) => cp.id));
    }
  }, [project?.checkpoints]);

  const handleToggleCollapse = (checkpointId: string) => {
    setCollapsedCheckpoints((prevCollapsed) => {
      // Add null safety check
      const currentCollapsed = prevCollapsed || [];
      return currentCollapsed.includes(checkpointId)
        ? currentCollapsed.filter((id) => id !== checkpointId)
        : [...currentCollapsed, checkpointId];
    });

    // Call the API when checkpoint is clicked
    handleCheckpointClick(checkpointId);
  };

  // Calculate total points for a checkpoint's rubrics
  const calculateCheckpointTotalPoints = (checkpoint: any) => {
    if (!checkpoint?.rubrics || checkpoint?.rubrics.length === 0) {
      return 0;
    }

    return checkpoint?.rubrics.reduce(
      (checkpointTotal: number, rubric: any) => {
        if (!rubric?.criteria || rubric?.criteria.length === 0) {
          return checkpointTotal;
        }

        const rubricTotal = rubric?.criteria.reduce(
          (rubricSum: number, criterion: any) => {
            return rubricSum + (Number(criterion?.points) || 0);
          },
          0,
        );

        return checkpointTotal + rubricTotal;
      },
      0,
    );
  };

  const isSubmissionDeadlinePassed = (): boolean => {
    if (!project?.due_date) return false;
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const dueDate = new Date(project.due_date);
    const finalDate = new Date(
      dueDate.setDate(
        dueDate.getDate() +
          (project?.late_submission_allowed
            ? project?.late_submission_days_allowed || 0
            : 0),
      ),
    );
    finalDate.setHours(0, 0, 0, 0);

    return currentDate > finalDate;
  };
  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="gap-4">
            <CardHeader>
              <div className="flex">
                <div className="h-6 w-8 ">
                  <Flag className=" h-6 w-6 text-indigo-500" />
                </div>
                <CardTitle className="text-xl text-bits-blue font-semibold mt-0">
                  Checkpoints
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                const validCheckpoints = (project?.checkpoints || []).filter(
                  (checkpoint: any) => checkpoint !== null,
                );

                // Try to find submission from the state first, otherwise from project data
                const anySubmissionWithGrade =
                  Object.values(checkpointSubmissions).find(
                    (sub: any) => sub?.grade || sub?.submission?.grade,
                  ) || (project as any).Submission?.rows?.[0];

                const overallGrade =
                  anySubmissionWithGrade?.grade ||
                  anySubmissionWithGrade?.submission?.grade;

                return (
                  <>
                    {overallGrade && (
                      <div className="mb-4 p-4 bg-bits-blue/10 rounded-lg border border-bits-blue/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-bits-blue/20 rounded-full">
                              <Flag className="h-5 w-5 text-bits-blue" />
                            </div>
                            <div>
                              <h4 className="font-bold text-bits-blue">
                                Overall Project Grade
                              </h4>
                              <p className="text-sm text-bits-blue/70">
                                Based on your latest submitted workspace
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-bits-blue">
                              {overallGrade.totalScore || overallGrade.total_score} /{" "}
                              {project?.total_points || 100}
                            </div>
                            <div className="flex items-center justify-end gap-2 text-sm font-semibold text-bits-blue">
                              {(() => {
                                const totalScore = Number(overallGrade.totalScore || overallGrade.total_score || 0);
                                const maxScore = Number(project?.total_points || 100);
                                const percentage = maxScore > 0 ? ((totalScore / maxScore) * 100).toFixed(2) : "0.00";
                                return <span>{percentage}%</span>;
                              })()}
                              {(overallGrade.letterGrade || overallGrade.letter_grade) && (
                                <Badge className="bg-bits-blue text-white hover:bg-bits-blue/90 border-none h-5 px-1.5 py-0">
                                  {overallGrade.letterGrade || overallGrade.letter_grade}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {validCheckpoints.length > 0 ? (
                      validCheckpoints.map((checkpoint: any) => {
                        const isCollapsed = collapsedCheckpoints?.includes(
                          String(checkpoint.id),
                        );
                        const totalPoints =
                          calculateCheckpointTotalPoints(checkpoint);

                        return (
                          <Card
                            key={checkpoint?.id}
                            className="gap-0 border rounded-lg shadow-sm"
                          >
                            <CardHeader
                              className={`p-4 cursor-pointer ${checkpoint?.status?.toLowerCase() === "overdue" ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                              onClick={() => {
                                if (
                                  checkpoint?.status?.toLowerCase() !==
                                  "overdue"
                                ) {
                                  handleToggleCollapse(checkpoint?.id);
                                }
                              }}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                  <CardTitle className="text-lg text-bits-blue font-semibold">
                                    {checkpoint?.title}
                                    {checkpoint?.is_required && (
                                      <span
                                        className="text-red-500 ml-1"
                                        title="Required"
                                      >
                                        *
                                      </span>
                                    )}
                                  </CardTitle>
                                  {totalPoints > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs font-medium bg-blue-50 text-bits-blue border-bits-blue"
                                    >
                                      {totalPoints} points
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  {(() => {
                                    const status =
                                      checkpoint?.studentStatus?.toLowerCase();
                                    const submission =
                                      checkpointSubmissions[checkpoint?.id];
                                    const checkpointData =
                                      submission?.checkpoints?.find(
                                        (cp: any) =>
                                          String(cp.id) ===
                                          String(checkpoint?.id),
                                      );
                                    const checkpointScore =
                                      checkpointData?.progress?.rubricScores
                                        ?.totalScore;

                                    return (
                                      <>
                                        {status === "graded" &&
                                          checkpointScore !== undefined && (
                                            <span className="text-sm font-bold text-gray-700 mr-1">
                                              {checkpointScore} / {totalPoints}
                                            </span>
                                          )}
                                        <span
                                          className={`px-2 py-1 text-sm font-medium rounded-md ${
                                            status === "yet to start"
                                              ? "text-white bg-yellow-400"
                                              : status === "in_progress"
                                                ? "text-white bg-green-500"
                                                : status === "submitted"
                                                  ? "text-white bg-green-800"
                                                  : status === "graded"
                                                    ? "text-white bg-orange-600"
                                                    : status === "completed"
                                                      ? "text-white bg-green-300"
                                                      : ""
                                          }`}
                                        >
                                          {checkpoint?.studentStatus}
                                        </span>
                                      </>
                                    );
                                  })()}

                                  {isCollapsed ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronUp className="h-4 w-4" />
                                  )}
                                </div>
                              </div>
                            </CardHeader>

                            {!isCollapsed && (
                              <CardContent className="p-4 pt-0">
                                <div className="text-muted-foreground mb-4">
                                  <div
                                    className="text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                                    dangerouslySetInnerHTML={{
                                      __html: checkpoint.description || "-",
                                    }}
                                  />
                                </div>

                            {/* Submission Information */}
                            {checkpointSubmissions[checkpoint?.id] && (
                              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <h4 className="font-semibold text-bits-blue mb-3 flex items-center">
                                  <Upload className="h-4 w-4 mr-2" />
                                  Submission Details
                                </h4>

                                {(() => {
                                  // Get the checkpoint progress data from the API response
                                  const currentSubmission =
                                    checkpointSubmissions[checkpoint?.id];
                                  const checkpointData =
                                    currentSubmission.checkpoints?.find(
                                      (cp: any) =>
                                        String(cp.id) === String(checkpoint?.id),
                                    );
                                  const progress = checkpointData?.progress;
                                  const submissionData =
                                    currentSubmission.submission;

                                  if (
                                    !progress ||
                                    !progress.filesSubmitted ||
                                    !(progress.filesSubmitted.length > 0)
                                  )
                                    return (
                                      <div className="text-sm text-grey-600 text-center font-semibold">
                                        No Submissions done yet
                                      </div>
                                    );
                                  return (
                                    <div className="space-y-3">
                                      <div className="flex items-center text-sm gap-1">
                                        <span className="flex font-medium">
                                          Submitted by :{" "}
                                        </span>
                                        <span className="flex items-center">
                                          {progress.submittedBy?.name ||
                                          progress.submittedBy ===
                                            submissionData?.user?.id
                                            ? submissionData?.user?.name
                                            : "N/A"}
                                        </span>
                                      </div>

                                      <div className="flex items-center text-sm gap-1">
                                        <span className="flex font-medium">
                                          {" "}
                                          Submitted on :{" "}
                                        </span>
                                        <span className="flex items-center">
                                          {progress.submittedAt
                                            ? format(
                                                parseISO(progress.submittedAt),
                                                "dd MMM yyyy, hh:mm a",
                                              )
                                            : "N/A"}
                                        </span>
                                      </div>

                                      <div className="flex items-center text-sm gap-1">
                                        <span className="font-medium">
                                          Attempts :{" "}
                                        </span>
                                        <span>{progress.attempts || 0}</span>
                                      </div>

                                      {/* <div className="flex items-center justify-between text-sm">
                                        <span className="font-medium">Time spent:</span>
                                        <span>{progress.timeSpentMinutes ? `${Math.floor(progress.timeSpentMinutes / 60)}h ${progress.timeSpentMinutes % 60}m` : "N/A"}</span>
                                      </div> */}

                                      {/* Student Notes */}
                                      {progress.studentNotes && (
                                        <div className="text-sm">
                                          <span className="font-medium">
                                            Notes:
                                          </span>
                                          <p className="mt-1 text-gray-600 italic">
                                            "{progress.studentNotes}"
                                          </p>
                                        </div>
                                      )}

                                      {/* Submitted Files */}
                                      {progress.filesSubmitted &&
                                        progress.filesSubmitted.length > 0 && (
                                          <div className="text-sm">
                                            <span className="font-medium">
                                              Submitted files:
                                            </span>
                                            <div className="mt-2 space-y-1">
                                              {progress.filesSubmitted.map(
                                                (file: any, index: number) => (
                                                  <div
                                                    key={index}
                                                    className="flex items-center justify-between p-2 bg-white rounded border"
                                                  >
                                                    <span className="flex items-center">
                                                      <FileText className="h-4 w-4 mr-2 text-blue-600" />
                                                      {file.fileName}
                                                    </span>
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      className="h-7 text-xs"
                                                      onClick={() => {
                                                        const link =
                                                          document.createElement(
                                                            "a",
                                                          );
                                                        link.href =
                                                          file.presignedUrl?.presignedUrl;
                                                        link.download =
                                                          file.fileName;
                                                        link.setAttribute(
                                                          "target",
                                                          "_self",
                                                        );
                                                        document.body.appendChild(
                                                          link,
                                                        );
                                                        link.click();
                                                        document.body.removeChild(
                                                          link,
                                                        );
                                                      }}
                                                    >
                                                      <Download className="h-3 w-3 mr-1" />
                                                      Download
                                                    </Button>
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}

                                      {/* Instructor Feedback */}
                                      {progress.instructor_feedback && (
                                        <div className="text-sm">
                                          <span className="font-medium">
                                            Instructor Feedback:
                                          </span>
                                          <p className="mt-1 text-gray-600">
                                            "{progress.instructor_feedback}"
                                          </p>
                                        </div>
                                      )}

                                      {/* Grade Information */}
                                      {(() => {
                                        const checkpointGrade =
                                          submissionData?.grade?.rubricScores?.[
                                            checkpoint?.id
                                          ];
                                        if (!checkpointGrade) return null;

                                        return (
                                          <div className="mt-3 p-3 bg-green-50 rounded border border-green-200">
                                            <div className="flex items-center justify-between">
                                              <span className="font-medium text-green-800">
                                                Checkpoint Grade:
                                              </span>
                                              <span className="text-lg font-bold text-green-800">
                                                {
                                                  checkpointGrade.checkpointTotalScore
                                                }{" "}
                                                /{" "}
                                                {
                                                  checkpointGrade.checkpointMaxScore
                                                }
                                              </span>
                                            </div>
                                            {submissionData.grade.gradedAt && (
                                              <div className="text-xs text-green-600 mt-1">
                                                Graded on:{" "}
                                                {format(
                                                  parseISO(
                                                    submissionData.grade
                                                      .gradedAt,
                                                  ),
                                                  "dd MMM yyyy, hh:mm a",
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}

                            {activeSandboxCheckpoint === checkpoint?.id && (
                              <div className="mb-4">
                                <SandboxPanel
                                  lifecycle={sandboxLifecycle}
                                  onOpenSandbox={handleOpenSandbox}
                                  projectParams={project}
                                  agreed={agreed}
                                  setAgreed={setAgreed}
                                />
                              </div>
                            )}
                            {checkpoint?.status
                              ?.toLowerCase()
                              .includes("graded") ? (
                              <>
                                <div className=" p-4 rounded-md mb-4">
                                  <p className="text-lg text-gray-600 font-medium">
                                    <span className="text-3xl text-gray-800 bg font-bold">
                                      {checkpoint?.grade}
                                    </span>{" "}
                                    Grade
                                  </p>
                                  <Card className="flex-row p-4 mt-4 border-none bg-background items-center">
                                    <MessageSquare className="h-6 w-6 text-bits-warning" />
                                    <div>
                                      <p className="font-semibold text-base">
                                        Instructor Feedback
                                      </p>
                                      <p className="text-muted-foreground">
                                        {checkpoint?.instructorFeedback}
                                      </p>
                                    </div>
                                  </Card>
                                </div>
                                <div className="flex justify-between items-center text-sm text-gray-500">
                                  <p className="flex items-center font-bold text-gray-800 mt-4">
                                    <Calendar className="h-5 w-5 mr-1 text-gray-500" />
                                    {checkpoint?.due_date
                                      ? format(
                                          parseISO(checkpoint?.due_date),
                                          "dd MMM yyyy",
                                        )
                                      : "N/A"}
                                  </p>
                                  <div className="flex mt-2 mr-1 space-x-2 text-base font-semibold text-gray-700">
                                    <button className="flex items-center px-3 py-1 border rounded-lg shadow-md">
                                      <Eye className="h-4 w-4 mr-2" /> Review
                                    </button>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="flex justify-between items-center text-sm text-gray-500">
                                <p className="flex items-center font-bold text-gray-800 mt-4">
                                  <Calendar className="h-5 w-5 mr-1 text-gray-500" />
                                  {checkpoint?.due_date
                                    ? format(
                                        parseISO(checkpoint?.due_date),
                                        "dd MMM yyyy",
                                      )
                                    : "N/A"}
                                </p>
                                <div className="flex mt-2 mr-1 space-x-2 text-base font-semibold">
                                  {checkpoint?.status === "in_progress" ? (
                                    <div>
                                        <SandboxPanel
                                          lifecycle={sandboxLifecycle}
                                          onOpenSandbox={handleOpenSandbox}
                                          projectParams={project}
                                          agreed={agreed}
                                          setAgreed={setAgreed}
                                          launchLabel="Launch Sandbox"
                                        />
                                    </div>
                                  ) : checkpoint?.status === "submitted" ? (
                                    <>
                                      <button className="flex items-center px-3 py-1 text-gray-700 border rounded-lg shadow-md">
                                        <Eye className="h-4 w-4 mr-2" /> Review
                                      </button>
                                      <button className="flex items-center px-3 py-2 text-gray-700 border rounded-lg shadow-md">
                                        <RotateCcw className="h-4 w-4 mr-2" />{" "}
                                        Withdraw submission
                                      </button>
                                    </>
                                  ) : checkpoint?.status === "overdue" ? (
                                    <>
                                      <Badge
                                        variant="destructive"
                                        className="text-xs"
                                      >
                                        Overdue - Cannot Submit
                                      </Badge>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    );
                  })
                ) : (
                  <p className="text-muted-foreground">
                    No checkpoints available.
                  </p>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>
    </div>
    <div className="space-y-6">
      {/* Quick Actions */}
      <Card className="gap-2">
        <CardHeader>
          <CardTitle className="flex items-center text-xl text-bits-blue font-semibold">
            <Rocket className="h-6 w-6 mr-2 text-purple-500" />
            Quick Actions
          </CardTitle>
        </CardHeader>
            <CardContent className="space-y-3">
              {!isSubmissionDeadlinePassed() && (
                <>
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Remaining sandbox time: <span className="font-semibold text-foreground">{remainingSandboxTimeLabel}</span>
                  </div>
                  {/* 1st Step: Setup the local agent tunnel */}
                  {workspace.globalSandboxMode === "local" && (
                    <LocalRunButton projectId={project?.id} />
                  )}

                  {/* Agent Status Badge */}
                  {workspace.globalSandboxMode === "local" && workspace.workspaceType === "local" && (
                    <AgentStatusBadge projectId={project?.id || ""} />
                  )}

                  <SandboxPanel
                    lifecycle={sandboxLifecycle}
                    onOpenSandbox={handleOpenSandbox}
                    projectParams={project}
                    agreed={agreed}
                    setAgreed={setAgreed}
                    launchLabel="Launch Environment"
                    launchDisabledReason={launchDisabledReason}
                  />
                </>
              )}

              <Button
                variant="outline"
                className="w-full h-full p-3"
                onClick={handleDownloadResources}
              >
                <Download className="h-4 w-4 mr-2" />
                Download Datasets
              </Button>

              <Button
                variant="outline"
                className="w-full h-full p-3"
                onClick={() => {
                  navigate(`${paths.communication}?mode=messages&compose=true`);
                }}
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                Message
              </Button>
            </CardContent>
          </Card>

          {/* Resource Profile */}
          {project?.resourceProfile &&
            typeof project.resourceProfile === "object" &&
            project.resourceProfile !== null && (
              <Card className="gap-2">
                <CardHeader>
                  <CardTitle className="flex items-center text-xl text-bits-blue font-semibold">
                    <UserCircle className="h-6 w-6 mr-2 text-violet-500" />
                    Resource Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="flex">
                      <div className="flex space-x-2">
                        <User className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium text-gray-700">
                          Profile Name :
                        </span>
                      </div>
                      <span className="text-sm font-semibold ml-2">
                        {project.resourceProfile.name}
                      </span>
                    </div>

                    <div className="flex">
                      <div className="flex space-x-2">
                        <Server className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">
                          Resource Type:
                        </span>
                      </div>
                      <span className="text-sm font-semibold ml-2">
                        {project.resourceProfile.resource_type}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Cpu className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-gray-600">CPU:</span>
                      <span className="text-sm font-semibold">
                        {project.resourceProfile.cpu_cores} cores
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <HardDrive className="h-4 w-4 text-purple-500" />
                      <span className="text-sm text-gray-600">RAM:</span>
                      <span className="text-sm font-semibold">
                        {project.resourceProfile.ram_gb} GB
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <HardDrive className="h-4 w-4 text-orange-500" />
                      <span className="text-sm text-gray-600">Disk:</span>
                      <span className="text-sm font-semibold">
                        {project.resourceProfile.disk_gb} GB
                      </span>
                    </div>

                    {project.resourceProfile.packages ||
                    project.resourceProfile.custom_packages ? (
                      <div className="pt-2 border-t">
                        <div className="flex items-center space-x-2 mb-2">
                          <Package className="h-4 w-4 text-indigo-500" />
                          <span className="text-sm font-medium text-gray-700">
                            Pre-installed Packages:
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {project.resourceProfile.packages?.map(
                            (pkg: string, index: number) => (
                              <Badge
                                key={index}
                                variant="secondary"
                                className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200"
                              >
                                {pkg}
                              </Badge>
                            ),
                          )}
                          {project.resourceProfile?.custom_packages?.map(
                            (pkg: string, index: number) => (
                              <Badge
                                key={index}
                                variant="secondary"
                                className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200"
                              >
                                {pkg}
                              </Badge>
                            ),
                          )}
                        </div>
                      </div>
                    ) : null}

                    {"build_status" in project.resourceProfile && (
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-sm font-medium text-gray-700">
                          Build Status:
                        </span>
                        <Badge
                          variant={
                            project.resourceProfile.build_status === "COMPLETED"
                              ? "default"
                              : "secondary"
                          }
                          className={
                            project.resourceProfile.build_status === "COMPLETED"
                              ? "bg-green-600 text-white"
                              : "bg-yellow-100 text-yellow-800"
                          }
                        >
                          {project.resourceProfile.build_status}
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
        </div>
      </div>
    </div>
  );
};

export default function ProjectDetailsPage({}: {}) {
  const { user } = useAuth();
  const location = useLocation() as any;
  const [activeTab, setActiveTab] = useState<
    "details" | "checkpoints" | "files" | "submissions" | "discussion"
  >(() => {
    const returnTab = sessionStorage.getItem("project_return_tab");
    if (returnTab && ["details", "checkpoints", "files", "submissions", "discussion"].includes(returnTab)) {
      return returnTab as any;
    }
    return (location.state?.defaultTab as any) ?? "details";
  });

  useEffect(() => {
    const returnTab = sessionStorage.getItem("project_return_tab");
    if (
      returnTab &&
      ["details", "checkpoints", "files", "submissions", "discussion"].includes(
        returnTab,
      )
    ) {
      sessionStorage.removeItem("project_return_tab");
      setActiveTab(returnTab as any);
    }
  }, [location.pathname]);
  const { projectId } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => getProjectById(projectId || ""),
    enabled: !!projectId,
  });
  const navigate = useNavigate();
  // Get project data with safe fallback; cast to any to align with local mock shape
  const project: any = data as any;

  const { setIsPollingActive } = useJupyterWorkspace();

  useEffect(() => {
    // Only poll if we are on the checkpoints tab (where the sandbox actions are)
    setIsPollingActive(activeTab === "checkpoints");
    
    // Cleanup: resume polling when leaving the page (the context will then use route-based filtering)
    return () => setIsPollingActive(true);
  }, [activeTab, setIsPollingActive]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "not_started":
        return <AlertCircle className="h-5 w-5" />;
      case "in_progress":
        return <Clock className="h-5 w-5" />;
      case "submitted":
        return <CheckSquare className="h-5 w-5" />;
      case "graded":
        return <CheckCircle className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "not_started":
        return "text-muted-foreground";
      case "in_progress":
        return "text-bits-warning";
      case "submitted":
        return "text-bits-green";
      case "graded":
        return "text-bits-blue";
      default:
        return "text-muted-foreground";
    }
  };
  const handlePrevious = () => {
    navigate(`/`);
  };
  return (
    <div className="p-6 space-y-6 w-full h-full">
      {/* Header */}
      {isLoading ? (
        <div className="flex justify-center items-center w-full h-[60vh] bg-white mt-16 ">
          <Card className="px-8 py-6 flex justify-center items-center">
            <div className="flex items-center space-x-3">
              <span className="text-gray-600 font-medium">
                Fetching Project Details...
              </span>
            </div>
          </Card>
        </div>
      ) : error ? (
        <div className="flex flex-col justify-center w-full items-center h-[60vh] mt-16">
          <div className="text-center space-y-4 max-w-md">
            <div className="text-red-600 text-lg font-semibold">
              Error Loading Project
            </div>
            <div className="text-gray-600">
              {(error as any)?.response?.data?.data?.message ||
                (error as any)?.message ||
                "Failed to load project details"}
            </div>
            <button
              onClick={() => navigate(paths.root)}
              className="mt-4 px-4 py-2 bg-bits-blue text-white rounded-md hover:bg-bits-blue/90"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      ) : (
        <>
          <ProjectHeader
            title={project?.title || "Untitled Project"}
            subtitle={`${project?.subject || ""}${
              project?.subject && project?.instructor ? " • " : ""
            }${project?.instructor || ""}`}
            onBackClick={handlePrevious}
          >
            {/* Status and Grade Section */}
            <div className="flex items-center space-x-3">
              <div
                className={`flex bg-yellow-50 rounded-lg px-3 py-1 items-center space-x-2 ${getStatusColor(
                  project?.status,
                )}`}
              >
                {getStatusIcon(project?.status)}
                <span className="font-medium capitalize text-sm">
                  {project?.status?.replace("_", " ")}
                </span>
              </div>
              {project?.grade !== undefined && (
                <Badge className="bg-green-600 text-white">
                  {project?.grade}/{project?.totalPoints} pts
                </Badge>
              )}
            </div>
          </ProjectHeader>

          <div className="p-0 -mt-2 -mx-2">
            <TabNavigation
              tabs={[
                {
                  key: "details",
                  label: "Project Details",
                  icon: <FileText className="h-4 w-4" />,
                },
                {
                  key: "checkpoints",
                  label: "Checkpoints & Submissions",
                  icon: <CheckSquare className="h-4 w-4" />,
                },
                ...(project?.is_group_project
                  ? [
                      {
                        key: "discussion",
                        label: "Discussion Forum",
                        icon: <MessageSquare className="h-4 w-4" />,
                      },
                    ]
                  : []),
              ]}
              activeTab={activeTab}
              onTabChange={(tab) =>
                setActiveTab(tab as "details" | "checkpoints" | "discussion")
              }
              className="mb-6 mx-2"
            />
          </div>

          {activeTab === "details" && <ProjectDetails project={project} />}
          {activeTab === "checkpoints" && (
            <CheckpointsAndSubmissions project={project} />
          )}
          {activeTab === "discussion" && project?.is_group_project && (
            <ProjectMessagesChat projectId={projectId} />
          )}
        </>
      )}
    </div>
  );
}
